import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  useLoginMutation,
  useLogoutMutation,
  type LoginRequest,
} from "../api-data/iam/auth";
import { useGetProfileDetails, UserResponse } from "../api-data/iam/user";
import { useBrowserTracking } from "../hooks/useBrowserTracking";

interface AuthContextType {
  user: UserResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credential: LoginRequest, redirectTo?: string, state?: any) => Promise<void>;
  logout: () => void;
  error: string | null;
  clearError: () => void;
  loggingIn: boolean;
  /** True while the logged-in user must change their temporary password. */
  requiresPasswordChange: boolean;
  /** Phone number to pre-fill on the forced-reset page. */
  forcedResetMobile: string;
  /** Country code to pre-fill on the forced-reset page. */
  forcedResetCountryCode: string;
  /** Call this after a successful forced password change to lift the lock. */
  clearForcedReset: () => void;
}

const initialState: AuthContextType = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => Promise.resolve(),
  logout: () => null,
  error: null,
  clearError: () => { },
  loggingIn: false,
  requiresPasswordChange: false,
  forcedResetMobile: "",
  forcedResetCountryCode: "",
  clearForcedReset: () => { },
};

const AuthContext = createContext<AuthContextType>(initialState);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoggedout, setIsLoggedout] = useState<boolean>(false);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [forcedResetMobile, setForcedResetMobile] = useState("");
  const [forcedResetCountryCode, setForcedResetCountryCode] = useState("");
  const navigate = useNavigate();
  // const location = useLocation();

  // Initialize browser tracking
  const browserTracking = useBrowserTracking();

  const {
    data: userDetails,
    isSuccess,
    isFetching: userDetailsLoading,
  } = useGetProfileDetails();

  const isAuthenticated = useMemo(
    () => isSuccess && !isLoggedout,
    [isSuccess, isLoggedout],
  );

  const { mutateAsync: loginMutation, isPending } = useLoginMutation();

  const { mutateAsync: logoutMutation } = useLogoutMutation();

  // Initialize browser fingerprint on mount
  useEffect(() => {
    if (!browserTracking.isInitialized) {
      browserTracking.initializeFingerprint().catch((err) => {
        console.error("Failed to initialize browser tracking:", err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add user to session when authenticated
  useEffect(() => {
    console.log("AuthContext - Checking to add user to browser session");
    if (isAuthenticated && userDetails?.id && browserTracking.sessionId) {
      console.log("AuthContext - Adding user to browser session");
      browserTracking.addUserToSession(userDetails.id).catch((err) => {
        console.error("Failed to add user to browser session:", err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userDetails?.id, browserTracking.sessionId]);

  const login = async (credentials: LoginRequest, redirectTo?: string, state?: any) => {
    try {
      const response = await loginMutation({ request: credentials });
      setError(null);
      setIsLoggedout(false);

      // If the account requires a password change (temp password set by admin),
      // set the forced-reset flag in context then go straight to the reset page.
      if (response?.requiresPasswordChange) {
        setRequiresPasswordChange(true);
        setForcedResetMobile(credentials.phoneNumber);
        setForcedResetCountryCode(credentials.countryCode);
        navigate("/account/change-passcode/reset", {
          replace: true,
          state: {
            mobile: credentials.phoneNumber,
            countryCode: credentials.countryCode,
            forcedReset: true,
          },
        });
        return;
      }

      let redirect = "/";

      if (redirectTo && redirectTo !== "/") {
        redirect = redirectTo;
      }
      // OOTWUI-025: admins and field operators always land on their home page after login.
      if (homePathFor(response?.userType) !== "/") redirect = homePathFor(response?.userType);

      navigate(redirect, { replace: true, state });
    } catch (error: any) {
      setError(error.response?.data?.message || "Login failed");
    }
  };

  const logout = () => {
    logoutMutation()
      .then(async () => {
        // Clear forced-reset lock BEFORE navigating so BaseLayout doesn't
        // redirect back to the reset page and cause a flicker.
        setRequiresPasswordChange(false);
        setForcedResetMobile("");
        setForcedResetCountryCode("");
        setIsLoggedout(true);

        // Session remains active - users are never removed from session

        navigate("/", { replace: true });
      })
      .catch((error) => {
        console.error("Logout failed:", error);
        localStorage.clear();
        // Session remains active - users are never removed from session
      });
  };

  const clearForcedReset = () => {
    setRequiresPasswordChange(false);
    setForcedResetMobile("");
    setForcedResetCountryCode("");
  };

  const value: AuthContextType = {
    // When logged out, ignore any stale React Query cache and return null
    // immediately — this is what drives the role-tab in MobileNavBar.
    user: isLoggedout ? null : (userDetails || null),
    isAuthenticated,
    isLoading: userDetailsLoading,
    login,
    logout,
    error,
    clearError: () => setError(null),
    loggingIn: isPending,
    requiresPasswordChange,
    forcedResetMobile,
    forcedResetCountryCode,
    clearForcedReset,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/** OOTWUI-025: the staff quick access page for admins and field operators; "/" for everyone else. */
export const homePathFor = (userType?: string | null) =>
  userType === "INTERNAL_USER"
    ? "/admin/home"
    : userType === "FIELD_OPERATOR"
      ? "/field-operator/home"
      : "/";

// Custom hook for easier consumption
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const useUserDetailsInfo = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context.user;
};
